import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Phone, MapPin, Clock, ArrowRight } from 'lucide-react';
import { useI18n } from '../i18n/LanguageContext';
import { Reveal } from '../components/Reveal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: '', email: '', company: '', phone: '', subject: '', message: '' };

export default function Contact() {
  const { t, lang } = useI18n();
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((er) => ({ ...er, [name]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = t.contact.form.requiredName;
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = t.contact.form.requiredEmail;
    if (!form.message.trim()) e.message = t.contact.form.requiredMessage;
    return e;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const v = validate();
    setErrors(v);
    if (Object.keys(v).length) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { ...form, language: lang });
      toast.success(t.contact.form.successTitle, { description: t.contact.form.successBody });
      setForm(initial);
    } catch (err) {
      toast.error(t.contact.form.errorTitle, { description: t.contact.form.errorBody });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Header */}
      <section className="container-x pt-24 md:pt-36 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <Reveal className="md:col-span-8">
            <div className="kicker">{t.contact.kicker}</div>
            <h1 className="display-1 mt-6 text-forest text-balance">{t.contact.title}</h1>
          </Reveal>
          <Reveal delay={100} className="md:col-span-4">
            <p className="lead">{t.contact.intro}</p>
          </Reveal>
        </div>
      </section>

      {/* Form + Info */}
      <section className="container-x pb-24 md:pb-32">
        <div className="border-t border-forest/15 grid grid-cols-1 md:grid-cols-12 gap-0">
          {/* Info */}
          <div className="md:col-span-5 md:border-r border-forest/15 py-12 md:py-16 md:pr-12">
            <div className="space-y-10">
              <div>
                <div className="kicker !text-forest/55">{t.contact.info.addressLabel}</div>
                <p className="mt-3 font-serif text-xl text-forest whitespace-pre-line leading-snug">
                  {t.contact.info.address}
                </p>
              </div>
              <div>
                <div className="kicker !text-forest/55">{t.contact.info.phoneLabel}</div>
                <div className="mt-3 flex flex-col gap-1">
                  <a href="tel:+34937532612" className="font-serif text-xl text-forest hover:text-rust transition-colors" data-testid="contact-phone-1">
                    +34 93 753 26 12
                  </a>
                  <a href="tel:+34615198057" className="font-serif text-xl text-forest hover:text-rust transition-colors" data-testid="contact-phone-2">
                    +34 615 198 057
                  </a>
                </div>
              </div>
              <div>
                <div className="kicker !text-forest/55">{t.contact.info.emailLabel}</div>
                <a href="mailto:info@gravatsmaresme.com" className="mt-3 font-serif text-xl text-forest hover:text-rust transition-colors block" data-testid="contact-email">
                  info@gravatsmaresme.com
                </a>
              </div>
              <div>
                <div className="kicker !text-forest/55">{t.contact.info.hoursLabel}</div>
                <p className="mt-3 font-serif text-xl text-forest">{t.contact.info.hours}</p>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={onSubmit} noValidate className="md:col-span-7 py-12 md:py-16 md:pl-12 space-y-8" data-testid="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
              <Field
                label={t.contact.form.name + ' *'}
                name="name"
                value={form.name}
                onChange={onChange}
                error={errors.name}
                testid="form-name"
              />
              <Field
                label={t.contact.form.email + ' *'}
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                error={errors.email}
                testid="form-email"
              />
              <Field
                label={t.contact.form.company}
                name="company"
                value={form.company}
                onChange={onChange}
                testid="form-company"
              />
              <Field
                label={t.contact.form.phone}
                name="phone"
                value={form.phone}
                onChange={onChange}
                testid="form-phone"
              />
            </div>
            <Field
              label={t.contact.form.subject}
              name="subject"
              value={form.subject}
              onChange={onChange}
              testid="form-subject"
            />
            <Field
              label={t.contact.form.message + ' *'}
              name="message"
              as="textarea"
              rows={5}
              value={form.message}
              onChange={onChange}
              error={errors.message}
              testid="form-message"
            />
            <div className="pt-2">
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="contact-submit"
              >
                {submitting ? t.contact.form.sending : t.contact.form.send}
                {!submitting && <ArrowRight size={14} />}
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Map */}
      <section className="border-t border-forest/15" data-testid="map-section">
        <iframe
          title="Gravats Maresme — Vilassar de Dalt"
          src="https://www.google.com/maps?q=Manuel%20Ventura%20Campeny%208%2C%2008339%20Vilassar%20de%20Dalt%2C%20Barcelona&output=embed"
          className="w-full h-[420px] md:h-[520px] grayscale-[40%] contrast-[1.05]"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </section>
    </div>
  );
}

function Field({ label, name, value, onChange, error, type = 'text', as = 'input', rows = 4, testid }) {
  const base =
    'w-full bg-transparent border-0 border-b border-forest/25 focus:border-forest focus:outline-none py-3 text-forest placeholder-forest/30 font-sans text-base transition-colors';
  return (
    <label className="block">
      <span className="kicker !text-forest/55 block">{label}</span>
      {as === 'textarea' ? (
        <textarea
          name={name}
          value={value}
          onChange={onChange}
          rows={rows}
          className={`${base} resize-none mt-1`}
          data-testid={testid}
        />
      ) : (
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          className={`${base} mt-1`}
          data-testid={testid}
        />
      )}
      {error && <span className="mt-2 block text-xs text-rust" data-testid={`${testid}-error`}>{error}</span>}
    </label>
  );
}
